import { useState, useEffect, useContext, useMemo } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { Box, Typography, Button, IconButton } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useIntl } from 'react-intl';
import MenuOutlined from '@ant-design/icons/MenuOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import useAuth from 'hooks/useAuth';
import TourContext from 'contexts/TourContext';
import { iconFor } from 'menu-items/dynamicMenu';
import WelcomeTourModal from './WelcomeTourModal';

// Custom Tooltip Component
const CustomTooltip = ({ continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps, size, isLastStep }) => {
  const intl = useIntl();

  const getStepIcon = () => {
    const iconStyle = { fontSize: 18 };
    // A configured tour carries its step's chosen icon (Advanced Settings →
    // Guided Tour) through step.data.icon; render that glyph. The built-in
    // default tour has no data, so it falls back to the per-index icons below.
    const configured = step?.data?.icon;
    if (configured) {
      const Glyph = iconFor(configured);
      return <Glyph style={iconStyle} />;
    }
    switch (index) {
      case 0:
        return <MenuOutlined style={iconStyle} />;
      case 1:
        return <MenuOutlined style={iconStyle} />;
      case 2:
        return <UserOutlined style={iconStyle} />;
      case 3:
        return <AppstoreOutlined style={iconStyle} />;
      default:
        return <MenuOutlined style={iconStyle} />;
    }
  };

  return (
    <Box
      {...tooltipProps}
      sx={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        // Fit within a 320px phone viewport (with gutters); roomier on larger screens
        width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
        maxWidth: { xs: 'calc(100vw - 24px)', sm: 380 },
        minWidth: { xs: 0, sm: 300 },
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        position: 'relative'
      }}
    >
      {/* Close button */}
      <IconButton
        {...closeProps}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: '#999',
          '&:hover': { color: '#666' }
        }}
      >
        <CloseOutlined style={{ fontSize: 14 }} />
      </IconButton>

      {/* Content */}
      <Box sx={{ p: { xs: 2, sm: 2.5 }, pt: 2 }}>
        {/* Step counter */}
        <Typography variant="caption" sx={{ color: '#999', fontSize: 12, mb: 1, display: 'block' }}>
          {intl.formatMessage({ id: 'tour.stepOf' }, { step: index + 1, total: size })}
        </Typography>

        {/* Title with icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#3d7a89',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff'
            }}
          >
            {step.isLastStep ? <CheckOutlined style={{ fontSize: 18 }} /> : getStepIcon()}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: 18 }}>
            {step.title}
          </Typography>
        </Box>

        {/* Description */}
        {step.content && (
          <Typography
            sx={{
              color: '#666',
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: 'pre-line',
              mb: step.isLastStep ? 1 : 0
            }}
          >
            {step.content}
          </Typography>
        )}

        {/* Success message for last step */}
        {step.isLastStep && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: '#3d7a89',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              <CheckOutlined style={{ fontSize: 14 }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: 15 }}>{intl.formatMessage({ id: 'tour.allSet' })}</Typography>
          </Box>
        )}
      </Box>

      {/* Footer with buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          borderTop: '1px solid #f0f0f0'
        }}
      >
        <Button
          {...skipProps}
          sx={{
            color: '#999',
            textTransform: 'none',
            fontSize: 14,
            '&:hover': { backgroundColor: 'transparent', color: '#666' }
          }}
        >
          {intl.formatMessage({ id: 'tour.skip' })}
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {index > 0 && (
            <Button
              {...backProps}
              sx={{
                color: '#3d7a89',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                '&:hover': { backgroundColor: 'rgba(61, 122, 137, 0.08)' }
              }}
            >
              {intl.formatMessage({ id: 'tour.back' })}
            </Button>
          )}
          <Button
            {...primaryProps}
            variant="contained"
            sx={{
              backgroundColor: '#3d7a89',
              color: '#fff',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 500,
              px: { xs: 2, sm: 3 },
              borderRadius: 2,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#2d6a79' }
            }}
          >
            {isLastStep ? intl.formatMessage({ id: 'tour.end' }) : intl.formatMessage({ id: 'tour.next' })}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const TOUR_STORAGE_KEY = 'guided_tour_completed';

export default function GuidedTour({ forceRun = false }) {
  const { user } = useAuth();
  const intl = useIntl();
  const tourContext = useContext(TourContext);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // The resolved tour is owned by TourProvider so the header bulb and this runner
  // agree on a single source of truth (see contexts/TourContext.jsx):
  //  - backendTour  : the enabled, configured tour to run; null when none applies
  //                   (disabled OR not configured OR backend unreachable).
  //  - tourResolved : the backend has answered; hold the auto-show until it has.
  const backendTour = tourContext?.backendTour || null;
  const tourResolved = Boolean(tourContext?.tourResolved);

  const downSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // The tour is ENTIRELY admin-configured — there is no built-in/hardcoded tour.
  // Each configured step maps straight onto Joyride; a step whose target element
  // isn't on the page is shown centred so it never fails to render. When nothing
  // is configured/enabled, steps is empty and no tour runs.
  const steps = useMemo(() => {
    if (!backendTour?.steps?.length) return [];
    return backendTour.steps.map((s, i) => {
      // Pass the configured CSS selector straight to react-joyride, which positions
      // the tooltip against the LIVE element and applies the chosen placement. A
      // step with NO target is anchored to the body and shown centred. (We do not
      // pre-check visibility here — that snapshotted the DOM once at load time,
      // before layout settled, and wrongly froze real targets as centred bubbles.)
      const target = (s.target || '').trim();
      return {
        target: target || 'body',
        title: s.title,
        content: s.content || '',
        placement: target ? (s.placement && s.placement !== 'auto' ? s.placement : 'auto') : 'center',
        // Carry the step's configured icon to CustomTooltip.getStepIcon.
        data: { icon: s.icon || null },
        disableBeacon: true,
        isLastStep: i === backendTour.steps.length - 1
      };
    });
  }, [backendTour]);

  // Listen for tour context changes (when user clicks the bulb icon)
  useEffect(() => {
    if (tourContext?.runTour) {
      tourContext.stopTour();
      // Nothing to start unless an enabled tour is configured for this scope.
      if (!backendTour) return;
      setStepIndex(0);
      setShowWelcomeModal(true);
    }
    // Intentionally keyed only on runTour/tourKey so the tour re-triggers on demand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourContext?.runTour, tourContext?.tourKey]);

  useEffect(() => {
    if (forceRun) {
      setShowWelcomeModal(true);
      setStepIndex(0);
      return undefined;
    }
    // Purely config-driven: only auto-show when an ENABLED tour (steps and/or an
    // intro page) is configured for this user's scope. No configured tour → nothing.
    if (!user || !tourResolved || !backendTour) return undefined;

    // A tour set to "every login" ignores the once-only completion flag.
    const everyLogin = backendTour?.when_to_show === 'every_login';
    const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY);

    if (everyLogin || !tourCompleted) {
      // Small delay to ensure page is loaded
      const timer = setTimeout(() => {
        setShowWelcomeModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [user, forceRun, backendTour, tourResolved]);

  const handleSkipWelcome = () => {
    setShowWelcomeModal(false);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  };

  const handleStartTour = () => {
    setShowWelcomeModal(false);
    setRun(true);
  };

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Update step index
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour finished or skipped
      setRun(false);
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    }
  };

  const joyrideStyles = {
    options: {
      arrowColor: '#fff',
      backgroundColor: '#fff',
      // Dim the page enough to focus the spotlight, but keep the surrounding UI
      // visible so users can see what the step is pointing at (0.9 blacked it out).
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      primaryColor: '#3d7a89',
      textColor: '#333',
      zIndex: 10000,
      spotlightShadow: '0 0 20px rgba(255, 255, 255, 0.3)'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    },
    spotlight: {
      borderRadius: 8
    },
    tooltip: {
      borderRadius: 12,
      padding: 0,
      maxWidth: downSM ? 'calc(100vw - 24px)' : 380,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
    },
    tooltipContainer: {
      textAlign: 'left',
      padding: '20px 24px'
    },
    tooltipTitle: {
      fontSize: 18,
      fontWeight: 600,
      marginBottom: 8,
      color: '#1a1a1a',
      display: 'flex',
      alignItems: 'center',
      gap: 10
    },
    tooltipContent: {
      fontSize: 14,
      lineHeight: 1.6,
      color: '#666',
      whiteSpace: 'pre-line'
    },
    tooltipFooter: {
      marginTop: 16,
      padding: '0'
    },
    buttonNext: {
      backgroundColor: '#3d7a89',
      borderRadius: 6,
      color: '#fff',
      padding: '10px 20px',
      fontSize: 14,
      fontWeight: 500
    },
    buttonBack: {
      color: '#3d7a89',
      marginRight: 10,
      fontSize: 14,
      fontWeight: 500
    },
    buttonSkip: {
      color: '#999',
      fontSize: 14
    },
    buttonClose: {
      color: '#999',
      top: 12,
      right: 12
    }
  };

  return (
    <>
      <WelcomeTourModal
        open={showWelcomeModal}
        onSkip={handleSkipWelcome}
        onStartTour={handleStartTour}
        title={backendTour?.intro_title || undefined}
        description={backendTour?.intro_description || undefined}
        allowSkip={backendTour ? backendTour.allow_skip !== false : true}
      />
      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={run}
        scrollToFirstStep
        showProgress
        showSkipButton={backendTour ? backendTour.allow_skip !== false : true}
        stepIndex={stepIndex}
        steps={steps}
        styles={joyrideStyles}
        tooltipComponent={CustomTooltip}
        floaterProps={{
          disableAnimation: true,
          styles: {
            arrow: {
              length: 8,
              spread: 12
            }
          }
        }}
        locale={{
          back: intl.formatMessage({ id: 'tour.back' }),
          close: intl.formatMessage({ id: 'common.close' }),
          last: intl.formatMessage({ id: 'tour.end' }),
          next: intl.formatMessage({ id: 'tour.next' }),
          skip: intl.formatMessage({ id: 'tour.skip' })
        }}
      />
    </>
  );
}

// Helper function to restart tour
export const restartTour = () => {
  localStorage.removeItem(TOUR_STORAGE_KEY);
  window.location.reload();
};

// Helper function to check if tour was completed
export const isTourCompleted = () => {
  return localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
};
